/**
 * Self Improvement Loop — revision strategies.
 *
 * One strategy per Art Director finding, keyed by rule id. Each names exactly
 * which part of the plan it repairs and repairs only that: a poor CTA improves
 * the CTA strategy, it does not touch colour; a poor hierarchy improves the
 * hierarchy, it does not re-language the design.
 *
 * A strategy returns `decisions` (improved plan decisions) and/or `patterns`
 * (an improved selection). Every improvement is derived from the inputs the
 * engine already has — genome, research, memory, patterns — so it is grounded
 * and deterministic. No AI, no randomness, no design.
 *
 * Findings with no strategy are deliberately unaddressable: they call for a
 * different visual language, which is the Thinking Engine's decision to make.
 */

import { decision, list, union, humanize } from '../thinking/reasoning';
import { HIERARCHY_ROLES } from '../thinking';
import { COMPOSITION_LIMITS, LANGUAGE_COHERENCE, MEDIUM_COHERENCE } from '../artDirector';
import { getPattern } from '../library';
import { CTA_ESCALATION, DEFAULT_CTA } from './revisionSchema';

/* ---------------------------------------------------------------- helpers */

const choiceOf = (plan, key) => (plan && plan[key] ? plan[key].choice : undefined);
const listChoice = (plan, key) => { const c = choiceOf(plan, key); return Array.isArray(c) ? c : []; };
const because = (rule) => `review:${rule}`;

/** Snap the named decisions back onto the genome's language. */
const realign = (ctx, map, rule, note) =>
  Object.entries(map)
    .filter(([key, dim]) => ctx.genome[dim] !== undefined && choiceOf(ctx.plan, key) !== ctx.genome[dim])
    .map(([key, dim]) => ({
      key,
      ...decision(
        ctx.genome[dim],
        `Return ${dim} to ${humanize(ctx.genome[dim])} — ${note} A design speaks one language, and this is the ${ctx.genome.name} language.`,
        [because(rule), `genome:${dim}`],
      ),
    }));

/* -------------------------------------------------------------- strategies */

export const STRATEGIES = Object.freeze([
  {
    rule: 'weak-hierarchy',
    targets: ['hierarchyStrategy'],
    apply: (ctx) => {
      const roles = ctx.offerLed ? HIERARCHY_ROLES.offerLed : HIERARCHY_ROLES.messageLed;
      // Keep what the plan already intended to lead with, then deepen beneath it.
      const ranked = union(listChoice(ctx.plan, 'hierarchyStrategy'), roles);
      return {
        decisions: [{
          key: 'hierarchyStrategy',
          ...decision(
            ranked,
            `Deepen the focal order to ${ranked.join(' → ')}. ${ctx.offerLed ? 'The offer still leads' : 'The headline still leads'}, but the eye now has somewhere to go after it, and the ${humanize(ctx.genome.hero)} hero holds the single focal point.`,
            [because('weak-hierarchy'), 'genome:hero'],
          ),
        }],
      };
    },
  },
  {
    rule: 'weak-cta',
    targets: ['ctaStrategy'],
    apply: (ctx) => {
      const current = choiceOf(ctx.plan, 'ctaStrategy');
      const next = CTA_ESCALATION[current] || DEFAULT_CTA;
      return {
        decisions: [{
          key: 'ctaStrategy',
          ...decision(
            next,
            `Escalate the call to action to ${humanize(next)}${current ? ` from ${humanize(current)}` : ''} — ${ctx.offerLed ? 'an offer must be answerable at a glance' : 'the design has to ask for something'}, so the action takes high contrast at the end of the reading flow.`,
            [because('weak-cta'), 'research:ctaBehaviour'],
          ),
        }],
      };
    },
  },
  {
    rule: 'weak-storytelling',
    targets: ['visualNarrative'],
    apply: (ctx) => {
      const order = listChoice(ctx.plan, 'hierarchyStrategy');
      const flow = order.length ? order.join(' → ') : 'headline → hero → cta';
      return {
        decisions: [{
          key: 'visualNarrative',
          ...decision(
            `Guide the eye ${flow}`,
            `Give the work an arc: the viewer enters at ${order[0] || 'the headline'}, is carried through ${flow}, and leaves having acted. The ${humanize(choiceOf(ctx.plan, 'compositionStrategy'))} composition carries that path.`,
            [because('weak-storytelling'), 'plan:hierarchyStrategy'],
          ),
        }],
      };
    },
  },
  {
    rule: 'weak-emotional-direction',
    targets: ['creativeDirection'],
    apply: (ctx) => ({
      decisions: [{
        key: 'creativeDirection',
        ...decision(
          `A ${ctx.genome.visualPhilosophy} direction`,
          `Name the feeling the work must land: ${ctx.genome.visualPhilosophy}. It is the emotional core of the ${ctx.genome.name} language, and every strategy below now has something to aim at.`,
          [because('weak-emotional-direction'), 'genome:visualPhilosophy'],
        ),
      }],
    }),
  },
  {
    rule: 'weak-premium-feel',
    targets: ['premiumCues'],
    apply: (ctx) => {
      // Restore the full set the genome and research already argued for.
      const cues = union(listChoice(ctx.plan, 'premiumCues'), ctx.genome.premiumCues, ctx.research.premiumCues);
      return {
        decisions: [{
          key: 'premiumCues',
          ...decision(
            cues,
            `Signal quality through ${cues.map(humanize).join(', ')} — the ${ctx.genome.name} cues combined with what research says reads as premium in this market. Craft, not decoration.`,
            [because('weak-premium-feel'), 'genome:premiumCues', 'research:premiumCues'],
          ),
        }],
      };
    },
  },
  {
    rule: 'weak-visual-language',
    targets: Object.keys(LANGUAGE_COHERENCE),
    apply: (ctx) => ({
      decisions: realign(ctx, LANGUAGE_COHERENCE, 'weak-visual-language', 'the plan had drifted from its own language.'),
    }),
  },
  {
    rule: 'incoherent-medium',
    targets: Object.keys(MEDIUM_COHERENCE),
    apply: (ctx) => ({
      decisions: realign(ctx, MEDIUM_COHERENCE, 'incoherent-medium', 'imagery, shape and decoration must reinforce the language rather than dilute it.'),
    }),
  },
  {
    rule: 'weak-originality',
    targets: ['variationAxes'],
    apply: (ctx) => {
      const axes = union(
        listChoice(ctx.plan, 'variationAxes'),
        ctx.constraints.generationConstraints && ctx.constraints.generationConstraints.recommendedVariationAxes,
      );
      return {
        decisions: [{
          key: 'variationAxes',
          ...decision(
            axes,
            `Push deliberately on ${axes.join(', ') || 'composition and narrative'} — memory shows these dimensions are where this concept resembles existing work, so this is where it must differ.`,
            [because('weak-originality'), 'memory:recommendedVariationAxes'],
          ),
        }],
      };
    },
  },
  {
    rule: 'wrong-pattern-selection',
    targets: [],
    apply: (ctx) => ({
      // Drop what does not belong to the language; keep everything that does.
      patterns: ctx.patterns.filter((p) => !Array.isArray(p.idealGenomes) || !ctx.genome.id || p.idealGenomes.includes(ctx.genome.id)),
      note: `Dropped patterns that do not belong to the ${ctx.genome.name} language.`,
    }),
  },
  {
    rule: 'conflicting-patterns',
    targets: [],
    apply: (ctx) => ({
      // Patterns arrive in relevance order, so the more relevant side of a conflict survives.
      patterns: ctx.patterns.reduce(
        (kept, p) => (kept.some((k) => list(k.incompatiblePatterns).includes(p.id) || list(p.incompatiblePatterns).includes(k.id)) ? kept : [...kept, p]),
        [],
      ),
      note: 'Dropped the less relevant side of each pattern conflict; a composition cannot honour both.',
    }),
  },
  {
    rule: 'too-much-complexity',
    targets: [],
    apply: (ctx) => ({
      patterns: ctx.patterns.slice(0, COMPOSITION_LIMITS.maxPatterns),
      note: `Cut to the ${COMPOSITION_LIMITS.maxPatterns} most relevant patterns — everything that does not earn its place dilutes what does.`,
    }),
  },
  {
    rule: 'too-much-simplicity',
    targets: [],
    apply: (ctx) => {
      // Top up from the patterns the genome itself recommends.
      const have = new Set(ctx.patterns.map((p) => p.id));
      const additions = list(ctx.genome.libraryPatterns)
        .filter((id) => !have.has(id))
        .map(getPattern)
        .filter(Boolean)
        .slice(0, Math.max(0, COMPOSITION_LIMITS.minPatterns - ctx.patterns.length));
      if (additions.length === 0) return {};
      return {
        patterns: [...ctx.patterns, ...additions],
        note: `Added ${additions.map((p) => p.name).join(', ')} from the ${ctx.genome.name} language — restraint is not the same as emptiness.`,
      };
    },
  },
]);

/** Strategy lookup by Art Director rule id. */
export const STRATEGY_BY_RULE = Object.freeze(
  STRATEGIES.reduce((acc, s) => ({ ...acc, [s.rule]: s }), {}),
);
